import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Chip, Grid, Paper, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { FilterContext } from '../../contexts/FilterContext';
import fetchData from '../../helpers/fetchData';
import { tokens } from '../../theme';
import ChannelTestTimelineChart, { ChannelTestDailyPoint } from './ChannelTestTimelineChart';

interface ZTestResult {
  p1: number;
  p2: number;
  diff: number;
  z: number | null;
  pValue: number | null;
  ci95Low: number | null;
  ci95High: number | null;
  nRequiredPerArm: number | null;
  significant: boolean;
}

interface ChannelTestArm {
  bucket: 'A' | 'B';
  qualifying_impressions: number;
  wall_doi: number;
  ad_doi: number;
  total_doi: number;
  total_rate_pct: number | null;
  wall_rate_pct: number | null;
  ad_rate_pct: number | null;
}

interface ChannelTestSummary {
  arms: ChannelTestArm[];
  zTestTotal: ZTestResult | null;
  sampleGuard: {
    thresholdPerArm: number;
    armATotalDoi: number;
    armBTotalDoi: number;
    cleared: boolean;
  };
}

const formatPct = (value: number | null, digits = 3): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${value.toLocaleString('de-DE', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })} %`;
};

const formatPp = (value: number | null, digits = 2): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const pp = value * 100;
  const sign = pp > 0 ? '+' : '';
  return `${sign}${pp.toLocaleString('de-DE', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })} pp`;
};

const formatPValue = (p: number | null): string => {
  if (p === null || p === undefined || !Number.isFinite(p)) return '—';
  if (p < 0.001) return 'p < 0,001';
  return `p = ${p.toLocaleString('de-DE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`;
};

const formatInt = (value: number): string => value.toLocaleString('de-DE');

const ArmCard: React.FC<{
  arm: ChannelTestArm;
  accentColor: string;
  description: string;
}> = ({ arm, accentColor, description }) => (
  <Paper elevation={3} sx={{ p: 2.5, borderLeft: `4px solid ${accentColor}` }}>
    <Box display="flex" alignItems="center" gap={1} mb={1}>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        Variante {arm.bucket}
      </Typography>
      <Chip label={description} size="small" />
    </Box>
    <Grid container spacing={1.5} columns={12}>
      <Grid item xs={6}>
        <Typography variant="caption" color="text.secondary">
          Qualifying-Impressions (GA4)
        </Typography>
        <Typography variant="h6">{formatInt(arm.qualifying_impressions)}</Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="caption" color="text.secondary">
          Gesamt-DOI
        </Typography>
        <Typography variant="h6">{formatInt(arm.total_doi)}</Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="caption" color="text.secondary">
          DOI via Leadwall
        </Typography>
        <Typography variant="h6">{formatInt(arm.wall_doi)}</Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="caption" color="text.secondary">
          DOI via Einzel-Analyse-Ad
        </Typography>
        <Typography variant="h6">{formatInt(arm.ad_doi)}</Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="caption" color="text.secondary">
          Hauptmetrik: DOI / Qualifying-Impression
        </Typography>
        <Typography variant="h4" sx={{ color: accentColor, fontWeight: 700 }}>
          {formatPct(arm.total_rate_pct)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Wall-Anteil {formatPct(arm.wall_rate_pct)} · Ad-Anteil {formatPct(arm.ad_rate_pct)}
        </Typography>
      </Grid>
    </Grid>
  </Paper>
);

const LeadwallChannelTestPage: React.FC = () => {
  const { filterState } = useContext(FilterContext)!;
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [summary, setSummary] = useState<ChannelTestSummary | null>(null);
  const [timeline, setTimeline] = useState<ChannelTestDailyPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const queryParams = useMemo(() => {
    if (!filterState.startDate || !filterState.endDate) return null;
    return {
      startDate: dayjs(filterState.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(filterState.endDate).format('YYYY-MM-DD'),
    };
  }, [filterState.startDate, filterState.endDate]);

  useEffect(() => {
    if (!queryParams) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [summaryResp, timelineResp] = await Promise.all([
          fetchData<ChannelTestSummary>(
            '/api/leadwall-channel-test/summary',
            queryParams,
            false,
          ) as Promise<ChannelTestSummary>,
          fetchData<ChannelTestDailyPoint>(
            '/api/leadwall-channel-test/timeline',
            queryParams,
          ) as Promise<ChannelTestDailyPoint[]>,
        ]);
        if (cancelled) return;
        setSummary(summaryResp);
        setTimeline(Array.isArray(timelineResp) ? timelineResp : []);
      } catch (err) {
        console.error('Error loading channel-test data:', err);
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unbekannter Fehler');
          setSummary(null);
          setTimeline([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [queryParams]);

  const armA = summary?.arms.find((a) => a.bucket === 'A');
  const armB = summary?.arms.find((a) => a.bucket === 'B');
  const zTest = summary?.zTestTotal ?? null;
  const guardCleared = summary?.sampleGuard.cleared ?? false;
  const guardThreshold = summary?.sampleGuard.thresholdPerArm ?? 400;

  // Significance card mirrors the existing Leadwall-Evaluation conventions:
  // a result only counts when (a) p < 0.05 AND (b) both arms cleared the
  // 400-DOI guard. Otherwise it's "noch nicht aussagekräftig".
  const significanceLabel = (() => {
    if (!zTest || !guardCleared) return 'Noch nicht aussagekräftig';
    if (zTest.significant) {
      return zTest.diff > 0 ? 'B gewinnt' : 'A gewinnt';
    }
    return 'Kein signifikanter Unterschied';
  })();

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Typography variant="h3" sx={{ fontWeight: 700 }}>
          Leadwall Akquise-Test
        </Typography>
        <Chip label="A: Leadwall · B: Einzel-Analyse-Ads" color="primary" />
      </Box>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Beide Arme messen pro qualifying Heavy-Reader-Visit (≥ 4. Artikel) wie viele Newsletter-DOIs
        zustande kommen. Variante A zeigt die Leadwall wie gewohnt; Variante B unterdrückt sie und
        vertraut auf die bestehenden Inline-/Bottom-/Mini-LP Einzel-Analyse-Ads. Adref-Token{' '}
        <code>LeadwallTest_A</code> / <code>LeadwallTest_B</code>
        attribuiert Ad-DOIs zum jeweiligen Arm.
      </Typography>

      {!guardCleared && summary && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Stichprobe noch zu klein für einen verlässlichen Sieger. Schwelle:{' '}
          <strong>{guardThreshold} DOI pro Arm</strong>. Aktuell: Arm A ={' '}
          {formatInt(summary.sampleGuard.armATotalDoi)}, Arm B ={' '}
          {formatInt(summary.sampleGuard.armBTotalDoi)}.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Lade Daten…
        </Alert>
      )}

      {summary && armA && armB && (
        <>
          <Grid container spacing={2} columns={12} mb={3}>
            <Grid item xs={12} md={6}>
              <ArmCard
                arm={armA}
                accentColor={colors.greenAccent?.[500] ?? '#4caf50'}
                description="Leadwall (A1)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ArmCard
                arm={armB}
                accentColor={colors.blueAccent?.[500] ?? '#2196f3'}
                description="Nur Einzel-Analyse-Ads"
              />
            </Grid>
          </Grid>

          <Paper elevation={2} sx={{ p: 2.5, mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }} mb={1}>
              Signifikanz (Hauptmetrik: Gesamt-DOI / Qualifying-Impression)
            </Typography>
            <Grid container spacing={2} columns={12}>
              <Grid item xs={12} md={3}>
                <Typography variant="caption" color="text.secondary">
                  Ergebnis
                </Typography>
                <Typography variant="h6">{significanceLabel}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="text.secondary">
                  Differenz (B − A)
                </Typography>
                <Typography variant="h6">{formatPp(zTest?.diff ?? null)}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="text.secondary">
                  P-Wert
                </Typography>
                <Typography variant="h6">{formatPValue(zTest?.pValue ?? null)}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="text.secondary">
                  Benötigt pro Arm
                </Typography>
                <Typography variant="h6">
                  {zTest?.nRequiredPerArm ? formatInt(zTest.nRequiredPerArm) : '—'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={2} sx={{ p: 2.5, mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }} mb={1}>
              Tagesverlauf — DOIs pro Arm
            </Typography>
            <Box height={420}>
              <ChannelTestTimelineChart points={timeline} />
            </Box>
          </Paper>

          <Alert severity="info">
            Sample-Size-Guard: ≥ {guardThreshold} DOI pro Arm. Quelle Hauptmetrik: GA4{' '}
            <code>leadwall_shown</code> (test_bucket = A) bzw.{' '}
            <code>leadwall_shown_counterfactual</code> als Nenner, plus{' '}
            <code>leadwall_signups</code> und <code>lps_email_meta_cache</code> mit{' '}
            <code>LeadwallTest_X</code> Adref-Token als Zähler.
          </Alert>
        </>
      )}
    </Box>
  );
};

export default LeadwallChannelTestPage;
